import {
  AuthMessage,
  AuthMessageType,
  ClientType,
  HostMessageType,
  PeerIdType,
  PresenceMessageType,
  websocket,
} from '@playcastdotio/Messaging';
import { bumpAuthDeadline, notifyHostAuthSuccess, requestPeerId, sendMessage, setIsReady, handleReconnect } from '..';
import { getStateStore } from '../../../stores';
import { queryOwnerAliases, queryProfile } from './profile';
import { sendMatchmakerHostConfig } from '../../managers/matchmaker';
import * as Sentry from '@sentry/react';
import constants from '@playcastdotio/GlobalConfig';
import { setAnalyticsIdentity } from '@playcastdotio/Analytics';
import { USE_SESSION_COORDINATOR, USE_MESSAGE_ROUTER } from '../../../../config/featureFlags';
import { appCoordinator } from '../../../../coordinators/init';
import { clearPendingHostAuth, getPendingHostAuth } from '../pendingHostAuth';
import { buildHostConnectAttestationProof } from '../../../services/hostConnectAttestationProof';
import { attachSentryTraceToMessage } from '../tracing';

/**
 * Attestation/connect failures that usually need host-side remediation (attest,
 * fix TPM/machineId) but must still retry — e.g. user may finish Add PC /
 * attestation while the socket is down, and nothing else calls hostConnect().
 * After several consecutive soft-fatals, floor reconnect delay to cut churn.
 */
const SOFT_FATAL_AUTH_ERROR_CODES = new Set([
  'NO_ATTESTATION',
  'INVALID_ATTESTATION_PROOF',
  'INVALID_MACHINE_ID',
  'PURPOSE_MISMATCH',
  'NONCE_EXPIRED',
  'NONCE_MISMATCH',
  'CHALLENGE_FAILED',
  'CHALLENGE_RATE_LIMITED',
  'PROOF_RATE_LIMITED',
]);

/** Allow a few normal-paced retries (onboarding race), then slow down. */
export const SOFT_FATAL_AUTH_SLOW_AFTER = 3;
export const SOFT_FATAL_AUTH_MIN_DELAY_MS = 60_000;

let consecutiveSoftFatalAuthFailures = 0;

/** Test-only: clear soft-fatal streak between cases. */
export const resetSoftFatalAuthFailureCountForTests = (): void => {
  consecutiveSoftFatalAuthFailures = 0;
};

const reconnectAfterSoftFatalAuth = (): void => {
  consecutiveSoftFatalAuthFailures += 1;
  if (consecutiveSoftFatalAuthFailures > SOFT_FATAL_AUTH_SLOW_AFTER) {
    handleReconnect({ minDelayMs: SOFT_FATAL_AUTH_MIN_DELAY_MS });
    return;
  }
  handleReconnect();
};

export const handleAuthMessage = (message: AuthMessage) => {
  console.log('Auth message:', message);
  switch (message.type) {
    case AuthMessageType.AUTH_CHALLENGE: {
      const pending = getPendingHostAuth();
      if (!pending?.rawSocket) {
        console.error('[Auth] AUTH_CHALLENGE received but no pending host auth context');
        Sentry.captureMessage('[Auth] AUTH_CHALLENGE without pending auth', { level: 'error' });
        break;
      }
      // Give AIK signing a fresh auth-deadline window; signing can take several seconds.
      bumpAuthDeadline();
      // Use server nonce only. Do not gate on client Date.now() vs expiresAt —
      // host clocks are often wrong; Redis TTL on realtime-api is nonce SoT.
      const { nonce } = message;
      const challengeSocket = pending.rawSocket;
      void (async () => {
        try {
          const attestationProof = await buildHostConnectAttestationProof({
            machineId: pending.machineId,
            nonce,
          });
          const current = getPendingHostAuth();
          if (
            !current ||
            current.rawSocket !== challengeSocket ||
            challengeSocket.readyState !== WebSocket.OPEN
          ) {
            console.warn('[Auth] AUTH_CHALLENGE proof ready but pending auth is stale; skipping send');
            return;
          }
          // Keep supportsAttestationChallenge on the proof retry (same transition as
          // connection.ts: tolerate old omitters → raise min registry version →
          // assume true server-side → stop sending the flag).
          const msg = pending.isGuestMode
            ? websocket.generators.auth(AuthMessageType.AUTH_REQUEST, {
                clientType: ClientType.HOST,
                machineId: pending.machineId,
                anonymous: true,
                cloudHostToken: pending.cloudHostToken,
                supportsAttestationChallenge: true,
                attestationProof,
              })
            : websocket.generators.auth(AuthMessageType.AUTH_REQUEST, {
                clientType: ClientType.HOST,
                tokenJwt: pending.accessToken,
                machineId: pending.machineId,
                cloudHostToken: pending.cloudHostToken,
                supportsAttestationChallenge: true,
                attestationProof,
              });
          attachSentryTraceToMessage(msg);
          challengeSocket.send(JSON.stringify(msg));
          console.log('[Auth] Re-sent AUTH_REQUEST with attestationProof after challenge');
        } catch (err) {
          console.error('[Auth] Failed to sign AUTH_CHALLENGE proof', err);
          Sentry.captureException(err, {
            tags: { issue_type: 'auth_challenge_sign_failed' },
          });
          const current = getPendingHostAuth();
          if (!current || current.rawSocket !== challengeSocket) {
            // Auth deadline (or another path) already cleared pending and owns reconnect.
            console.warn('[Auth] AUTH_CHALLENGE sign failed after auth attempt ended; skipping reconnect');
            return;
          }
          // Sign failure often needs TPM/Playjector recovery — still retry, but slow.
          // handleReconnect closes the socket; do not call disconnect() first (that
          // clears an armed reconnect timer and can strand retries).
          clearPendingHostAuth();
          handleReconnect({ minDelayMs: SOFT_FATAL_AUTH_MIN_DELAY_MS });
        }
      })();
      break;
    }
    case AuthMessageType.AUTH_ERROR: {
      const errorData = (message as AuthMessage & { error?: { code?: string; message?: string } }).error;
      const code = errorData?.code ?? 'UNKNOWN';
      const errorMessage = errorData?.message ?? 'Unknown auth error';

      console.error(`[Auth] AUTH_ERROR received: code=${code}, message=${errorMessage}`);
      Sentry.captureMessage(`[Auth] Server rejected authentication: ${code}`, {
        level: 'error',
        tags: { auth_error_code: code },
        extra: {
          errorMessage,
          guest_mode_enabled: getStateStore('ipc').hostMatchmakingState.currentState.guestAccount,
          edition: getStateStore('ipc').hostState.currentState.edition,
        },
      });

      clearPendingHostAuth();
      // handleReconnect closes the socket; avoid disconnect() here so we cannot
      // cancel an already-armed reconnect (e.g. auth deadline raced this error).
      if (SOFT_FATAL_AUTH_ERROR_CODES.has(code)) {
        reconnectAfterSoftFatalAuth();
      } else {
        consecutiveSoftFatalAuthFailures = 0;
        handleReconnect();
      }
      break;
    }
    case AuthMessageType.AUTH_SUCCESS: {
      console.log('Auth success');
      consecutiveSoftFatalAuthFailures = 0;
      clearPendingHostAuth();
      setIsReady(true);
      const state = getStateStore('ipc');

      // Apply server-confirmed cloud host status
      const isCloudHost = !!message.isCloudHost;
      if (isCloudHost) {
        console.log('[Auth] ✅ Server confirmed this host as a cloud host');
      }
      appCoordinator.updateCloudHostStatus(isCloudHost);

      // Server-resolved analytics segment. Set directly rather than through a
      // hostPresenceState listener, because that store is only written below
      // when SessionCoordinator is inactive. Written on every auth, including
      // when the server omits the field: passing `undefined` clears a prior
      // connection's value so a reconnect (or an older server) cannot keep
      // stamping a stale classification onto new events. The identity merge in
      // sendAnalytics skips `undefined`, so an omitted value produces no field.
      setAnalyticsIdentity('host', { hostClassification: message.hostClassification });

      notifyHostAuthSuccess();

      // STRANGLER-FIG: Skip PRESENCE_UPDATE when SessionCoordinator is active
      if (!USE_SESSION_COORDINATOR) {
        state.hostPresenceState.updateState({
          ...state.hostPresenceState.currentState,
          isCloudHost,
        });

        sendMessage(
          websocket.generators.presence(PresenceMessageType.PRESENCE_UPDATE, {
            clientType: ClientType.HOST,
            presence: {
              ...state.hostPresenceState.currentState,
              isCloudHost,
            },
          }),
        );
      }

      sendMessage(
        websocket.generators.host(HostMessageType.HOST_SETTINGS, {
          displayName: state.machineNameState.currentState,
          maxPlayerCount: state.hostSettingsState.currentState.maxPlayerCount,
          version: state.versionState.currentState.version,
          frontendVersion: constants.VERSION,
        }),
      );
      // const pingMap = state.networkMetricsState.currentState.ping;
      // if (Object.keys(pingMap).length > 0) {
      //   sendMessage(
      //     websocket.generators.vpn(VpnMessageType.VPN_CONNECT_REQUEST, {
      //       pingMap,
      //     }),
      //   );
      // } else {
      //   const waitForPingMap = async () => {
      //     // make a promise waiting for the ping map to be populated
      //     return await new Promise<LatencyResults>((resolve) => {
      //       const interval = setInterval(() => {
      //         const pingMap = state.networkMetricsState.currentState.ping;
      //         if (Object.keys(pingMap).length > 0) {
      //           clearInterval(interval);
      //           resolve(pingMap);
      //         }
      //       }, 1000);
      //     });
      //   };
      //   waitForPingMap().then((awaitedPingMap) => {
      //     if (awaitedPingMap) {
      //       sendMessage(
      //         websocket.generators.vpn(VpnMessageType.VPN_CONNECT_REQUEST, {
      //           pingMap: awaitedPingMap,
      //         }),
      //       );
      //     }
      //   });
      // }
      // console.log('Sending invitation store message from handleAuthMessage');
      // sendMessage(
      //   websocket.generators.invitation(InvitationMessageType.INVITATION_STORE, {
      //     invitation: {
      //       ...state.invitationStateV2.currentState,
      //       invalid:
      //         state.hostState.currentState.status === 'streaming' || state.hostState.currentState.status === 'starting' ? false : true,
      //     },
      //   }),
      // );
      // If we are already streaming, we need to request a new peer ID
      if (state.hostState.currentState.status === 'streaming') {
        requestPeerId(PeerIdType.HOST);
      }

      if (state.hostState.currentState.edition !== 'standard') {
        // Request profile of the user.
        queryProfile();
        queryOwnerAliases();

        // STRANGLER-FIG: Skip legacy matchmaker config when new coordinator architecture is active
        // PavilionCoordinator will handle HOST_CONFIG messages on its own
        if (!USE_MESSAGE_ROUTER) {
          // CRITICAL: Re-send matchmaking config after reconnection to preserve state
          // This ensures the host remains in the matchmaking pool after websocket reconnects
          console.log(`[Auth] Reconnected - re-sending matchmaking config for edition: ${state.hostState.currentState.edition}`);
          Sentry.addBreadcrumb({
            category: 'auth',
            message: 'Reconnected - re-sending matchmaking config',
            level: 'info',
            data: {
              edition: state.hostState.currentState.edition,
              matchmakeable: state.hostMatchmakingState.currentState.matchmakeable,
              // Guest mode context
              guest_mode_enabled: state.hostMatchmakingState.currentState.guestAccount,
              ad_mode_enabled: state.adModeState.currentState.enabled,
              game_launched: state.playjectorHostModeState.currentState.gameLaunched,
              user_logged_in: state.playjectorHostModeState.currentState.userLoggedIn,
              steam_launched: state.playjectorHostModeState.currentState.steamLaunched,
              host_status: state.hostState.currentState.status,
            },
          });
          sendMatchmakerHostConfig().catch((err) => {
            console.error('[Auth] Failed to send host config after auth:', err);
          });
        } else {
          console.log('[Auth] Strangler-fig enabled - PavilionCoordinator will handle HOST_CONFIG on reconnection');
        }
      }
    }
  }
};
